import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Row, Col, Button, Spin, message, Statistic, Space, Tooltip, Select, Popconfirm, Modal, Form, InputNumber, Checkbox, DatePicker, Input } from 'antd';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, CalendarOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { listUsers, listTemporaryUsers } from '../services/userService';
import { getAttendanceForMonth, markAttendance, updateMonthlyAttendance } from '../services/attendanceService';
import { getSalarySlip } from '../services/salarySlipService';
import { getSites } from '../services/siteService';
import { renderActivityDot } from '../utils/activityDot';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const AttendanceManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isSlipGenerated, setIsSlipGenerated] = useState(false);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);
  const [printData, setPrintData] = useState([]);
  const [isHolidayModalVisible, setIsHolidayModalVisible] = useState(false);
  const [selectedHolidayDate, setSelectedHolidayDate] = useState(null);
  const [holidayNarration, setHolidayNarration] = useState('');
  const [holidayIsPaid, setHolidayIsPaid] = useState(false);
  const [isBulkMarkingModalVisible, setIsBulkMarkingModalVisible] = useState(false);
  const [bulkMarkingStatus, setBulkMarkingStatus] = useState(null);
  const [bulkMarkingSite, setBulkMarkingSite] = useState(null);
  const [userTypeToggle, setUserTypeToggle] = useState('permanent'); // 'permanent' or 'temporary'

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (userTypeToggle === 'temporary') {
          const response = await listTemporaryUsers();
          setUsers(response.users || []);
        } else {
          const response = await listUsers();
          setUsers(response.users || []);
        }
      } catch (error) {
        messageApi.error('Failed to fetch users');
      }
    };
    fetchUsers();
  }, [userTypeToggle]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getSites();
        setSites(response.sites || []);
      } catch (error) {
        messageApi.error('Failed to fetch sites');
      }
    };
    fetchSites();
  }, []);

  const handleLoadAttendance = async () => {
    if (!selectedUser || !selectedMonth) {
      messageApi.warning('Please select a user and a month.');
      return;
    }
    setLoading(true);
    setMonthlyData([]);
    setIsSlipGenerated(false);

    try {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      // Check #2: See if salary slip is generated
      try {
        await getSalarySlip(selectedUser, year, month);
        setIsSlipGenerated(true);
        messageApi.error('Salary slip for this month has already been generated. Attendance cannot be modified.');
        setLoading(false);
        return;
      } catch (error) {
        if (error.response && error.response.status !== 404) {
          messageApi.error('Could not verify salary slip status. Please try again.');
          console.error(error);
          setLoading(false);
          return;
        }
        // A 404 error is expected and means we can proceed.
      }

      console.log('Calling getAttendanceForMonth with:', { selectedUser, year, month });
      
      
      const response = await getAttendanceForMonth(selectedUser, year, month);
      console.log('API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', response ? Object.keys(response) : 'No response');
      
      // Handle different possible response structures
      let attendanceRecords = [];
      if (response && response.attendanceRecords) {
        attendanceRecords = response.attendanceRecords;
        console.log('Found attendanceRecords in response root');
      } else if (response && response.data && response.data.attendanceRecords) {
        attendanceRecords = response.data.attendanceRecords;
        console.log('Found attendanceRecords in response.data');
      } else if (response && Array.isArray(response)) {
        attendanceRecords = response;
        console.log('Response is an array');
      } else if (response && response.attendance) {
        attendanceRecords = response.attendance;
        console.log('Found attendance in response root');
      } else {
        console.log('No attendance data found in response, using empty array');
        console.log('Full response for debugging:', JSON.stringify(response, null, 2));
        attendanceRecords = [];
      }
      
      console.log('Final attendance records:', attendanceRecords);
      
      const existingAttendance = attendanceRecords.reduce((acc, record) => {
        if (record.Status) {
          // Handle nested Status object format
          if (typeof record.Status === 'object' && record.Status.status) {
            acc[record.Date] = {
              status: record.Status.status,
              siteId: record.Status.siteId,
              extraShifts: record.Status.extraShifts || 0,
              narration: record.Status.narration || null,
              isPaidHoliday: record.Status.isPaidHoliday === true
            };
          } else if (typeof record.Status === 'string') {
            // Handle legacy string format
            acc[record.Date] = {
              status: record.Status,
              siteId: record.SiteId,
              extraShifts: record.ExtraShifts || 0,
              narration: record.Narration || null,
              isPaidHoliday: record.IsPaidHoliday === true
            };
          }
        }
        return acc;
      }, {});
      console.log('Parsed existing attendance:', existingAttendance);
      const daysInMonth = selectedMonth.daysInMonth();
      const newMonthlyData = [];
      const today = dayjs();
      for (let i = 1; i <= daysInMonth; i++) {
        const date = selectedMonth.clone().date(i);
        const dateString = date.format('YYYY-MM-DD');
        const attendanceInfo = existingAttendance[dateString] || {};
        
        newMonthlyData.push({
          key: dateString,
          date: date,
          status: attendanceInfo.status || 'NOT_MARKED',
          siteId: attendanceInfo.siteId || null,
          extraShifts: attendanceInfo.extraShifts || 0,
          narration: attendanceInfo.narration || null,
          isPaidHoliday: attendanceInfo.isPaidHoliday === true,
          disabled: date.isAfter(today, 'day')
        });
      }
      setMonthlyData(newMonthlyData);

    } catch (error) {
      console.error('Error loading attendance data:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      if (error.response && error.response.data && error.response.data.message) {
        messageApi.error(`Failed to load attendance data: ${error.response.data.message}`);
      } else if (error.message) {
        messageApi.error(`Failed to load attendance data: ${error.message}`);
      } else {
        messageApi.error('Failed to load attendance data. Please check the console for details.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (dateKey, newStatus) => {
    const newData = monthlyData.map(item =>
      item.key === dateKey ? { ...item, status: newStatus } : item
    );
    setMonthlyData(newData);
  };

  const handleSiteChange = (dateKey, newSiteId) => {
    const newData = monthlyData.map(item =>
      item.key === dateKey ? { ...item, siteId: newSiteId } : item
    );
    setMonthlyData(newData);
  };

  const handleExtraShiftsChange = (dateKey, extraShifts) => {
    const newData = monthlyData.map(item =>
      item.key === dateKey ? { ...item, extraShifts: extraShifts || 0 } : item
    );
    setMonthlyData(newData);
  };

  const handleMarkAllAsHoliday = () => {
    console.log('Mark All as Holiday clicked');
    if (users.length === 0) {
      messageApi.warning('No users available.');
      return;
    }

    console.log('Opening holiday modal');
    setIsHolidayModalVisible(true);
  };

  const handleHolidayDateChange = (date) => {
    console.log('Holiday date selected:', date);
    setSelectedHolidayDate(date);
  };

  const handleConfirmHoliday = () => {
    if (!selectedHolidayDate) {
      messageApi.warning('Please select a date first.');
      return;
    }
    
    console.log('Confirming holiday for date:', selectedHolidayDate);
    setIsHolidayModalVisible(false);
    handleMarkAllUsersHoliday(selectedHolidayDate);
  };

  const handleCancelHoliday = () => {
    setIsHolidayModalVisible(false);
    setSelectedHolidayDate(null);
    setHolidayNarration('');
    setHolidayIsPaid(false);
  };

  const handleBulkMarking = () => {
    if (!selectedUser) {
      messageApi.warning('Please select a user first.');
      return;
    }
    if (monthlyData.length === 0) {
      messageApi.warning('Please load attendance data first.');
      return;
    }
    
    setIsBulkMarkingModalVisible(true);
    setBulkMarkingStatus(null);
    setBulkMarkingSite(null);
  };

  const handleConfirmBulkMarking = () => {
    if (!bulkMarkingStatus) {
      messageApi.warning('Please select a status.');
      return;
    }

    const today = dayjs();

    // Update all dates that are currently NOT_MARKED and not in the future
    const newData = monthlyData.map(item => {
      // Only update if: status is NOT_MARKED, not disabled, and not a future date
      if (item.status === 'NOT_MARKED' && !item.disabled && dayjs(item.key).isSameOrBefore(today, 'day')) {
        return { 
          ...item, 
          status: bulkMarkingStatus,
          ...(bulkMarkingSite && { siteId: bulkMarkingSite })
        };
      }
      return item;
    });

    setMonthlyData(newData);
    setIsBulkMarkingModalVisible(false);
    setBulkMarkingStatus(null);
    setBulkMarkingSite(null);
    
    const unmarkedCount = monthlyData.filter(
      item => item.status === 'NOT_MARKED' && !item.disabled && dayjs(item.key).isSameOrBefore(today, 'day')
    ).length;
    
    const siteMessage = bulkMarkingSite ? ` with site assigned` : '';
    messageApi.success(`${unmarkedCount} unmarked date(s) updated to ${bulkMarkingStatus}${siteMessage}. Click "Update All Attendance" to save.`);
  };

  const handleCancelBulkMarking = () => {
    setIsBulkMarkingModalVisible(false);
    setBulkMarkingStatus(null);
    setBulkMarkingSite(null);
  };

  const handleMarkAllUsersHoliday = async (selectedDate) => {
    try {
      setLoading(true);
      
      // Get all users
      const allUsers = users;
      
      if (allUsers.length === 0) {
        messageApi.warning('No active users found.');
        return;
      }

      const dateString = selectedDate.format('YYYY-MM-DD');
      const year = selectedDate.year();
      const month = selectedDate.month() + 1;
      const day = selectedDate.date();

      // Mark each user as holiday for the selected date
      const promises = allUsers.map(async (user) => {
        try {
          const attendancePayload = {
            workerMobileNumber: user.MobileNumber,
            date: dateString,
            status: 'H',
            siteId: null,
            extraShifts: 0,
            narration: holidayNarration || null,
            isPaidHoliday: holidayIsPaid
          };
          console.log('Attendance Payload', attendancePayload);
          await markAttendance(attendancePayload);
          return { success: true, user: user.MobileNumber };
        } catch (error) {
          console.error(`Error marking holiday for user ${user.MobileNumber}:`, error);
          return { success: false, user: user.MobileNumber, error: error.message };
        }
      });

      const results = await Promise.all(promises);
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (successful > 0) {
        messageApi.success(`Successfully marked ${successful} users as holiday for ${dateString}`);
      }
      
      if (failed > 0) {
        messageApi.warning(`${failed} users could not be marked as holiday. Please check individual users.`);
      }

      // Refresh current user's attendance if it's loaded
      if (monthlyData.length > 0) {
        handleLoadAttendance();
      }

    } catch (error) {
      console.error('Error marking all users as holiday:', error);
      messageApi.error('Failed to mark users as holiday. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    const attendancePayload = monthlyData
        .filter(d => d.status !== 'NOT_MARKED')
        .reduce((acc, curr) => {
                    acc[dayjs(curr.key).date()] = {
            status: curr.status,
            siteId: curr.siteId,
            extraShifts: curr.extraShifts || 0,
            narration: curr.narration || null,
            isPaidHoliday: curr.status === 'H' ? (curr.isPaidHoliday === true) : false
        };
            return acc;
        }, {});

    const submissionData = {
        workerMobileNumber: selectedUser,
        year: selectedMonth.year(),
        month: selectedMonth.month() + 1,
        attendance: attendancePayload
    };

    try {
        await updateMonthlyAttendance(submissionData.workerMobileNumber, submissionData.year, submissionData.month, submissionData.attendance);
        messageApi.success("Attendance updated successfully!");
        handleLoadAttendance(); // Refresh data
    } catch (error) {
        messageApi.error("Failed to update attendance.");
        console.error(error);
    } finally {
        setSubmitting(false);
    }
  };

  const handleMonthChange = (value) => {
    const [year, month] = value.split('-');
    // Build the month from an explicit 1st-of-month date. Deriving it from
    // dayjs() would roll over when today's day-of-month doesn't exist in the
    // target month (e.g. picking June while today is the 31st).
    setSelectedMonth(dayjs(`${year}-${String(month).padStart(2, '0')}-01`));
    // Drop the loaded grid: it belongs to the previous month, and "Update All
    // Attendance" reads year/month at submit time, so keeping it would write
    // the old month's statuses onto this month's dates.
    setMonthlyData([]);
    setIsSlipGenerated(false);
  };

  // Generate month options for the last 2 years and current year
  const generateMonthOptions = () => {
    const options = [];
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month();
    
    // Add last 2 years
    for (let year = currentYear - 2; year <= currentYear; year++) {
      for (let month = 0; month < 12; month++) {
        const date = dayjs().year(year).month(month).startOf('month');
        if (date.isSameOrBefore(dayjs().endOf('month'))) {
          options.push({
            value: `${year}-${month + 1}`,
            label: date.format('MMMM YYYY')
          });
        }
      }
    }
    return options.reverse(); // Most recent first
  };

  const attendanceStatuses = ["P/2", "P", "P1/4", "P1/2", "PP", "PP1/2", "PPP", "PPP1/2", "PPPP", "A", "H", "NOT_MARKED"];

  const columns = [
    {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: (date) => date.format('ddd, DD MMM YYYY'),
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status, record) => {
            const user = users.find(u => u.MobileNumber === selectedUser);
            const isFixedUser = user && user.EmploymentType === 'FIXED';
            
            // For fixed users, only show A, P, H and NOT_MARKED options
            const availableStatuses = isFixedUser ? ["A", "P", "H", "NOT_MARKED"] : attendanceStatuses;
            
            // For fixed users, map partial present statuses to P for display
            const getSelectValue = (currentStatus, isFixedUser) => {
                if (isFixedUser && ['P/2', 'P1/4', 'P1/2', 'PP', 'PP1/2', 'PPP', 'PPP1/2', 'PPPP'].includes(currentStatus)) {
                    return 'P';
                }
                return currentStatus;
            };
            
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Select 
                        value={getSelectValue(status, isFixedUser)} 
                        onChange={(value) => {
                            const newData = monthlyData.map(item =>
                                item.key === record.key 
                                    ? { 
                                        ...item, 
                                        status: value,
                                        // Reset isPaidHoliday if status changes away from 'H'
                                        isPaidHoliday: value === 'H' ? item.isPaidHoliday : false
                                    } 
                                    : item
                            );
                            setMonthlyData(newData);
                        }}
                        style={{ width: 120 }}
                        disabled={record.disabled || isSlipGenerated}
                    >
                        {availableStatuses.map(s => <Option key={s} value={s}>{s}</Option>)}
                    </Select>
                    {status === 'H' && (
                        <Checkbox
                            checked={record.isPaidHoliday === true}
                            onChange={(e) => {
                                const newData = monthlyData.map(item =>
                                    item.key === record.key ? { ...item, isPaidHoliday: e.target.checked } : item
                                );
                                setMonthlyData(newData);
                            }}
                            disabled={record.disabled || isSlipGenerated}
                        >
                            Paid
                        </Checkbox>
                    )}
                    {record.narration && (
                        <div style={{ fontSize: '12px', color: themeConfig.text.muted, marginLeft: '8px' }}>
                            ({record.narration})
                        </div>
                    )}
                </div>
            );
        }
    },
    {
        title: 'Narration',
        dataIndex: 'narration',
        key: 'narration',
        render: (narration, record) => (
            <Input
                placeholder="Enter narration..."
                value={narration || ''}
                onChange={(e) => {
                    const newData = monthlyData.map(item =>
                        item.key === record.key ? { ...item, narration: e.target.value } : item
                    );
                    setMonthlyData(newData);
                }}
                disabled={record.disabled || isSlipGenerated}
                style={{ width: 200 }}
            />
        )
    },
    {
        title: 'Site',
        dataIndex: 'siteId',
        key: 'siteId',
        render: (siteId, record) => (
            <Select
                placeholder="Select Site"
                value={siteId || undefined}
                onChange={(value) => handleSiteChange(record.key, value)}
                style={{ width: 150 }}
                disabled={record.disabled || isSlipGenerated}
                allowClear
            >
                {sites.map(site => (
                    <Option key={site.id} value={site.id}>
                        {site.siteName}
                    </Option>
                ))}
            </Select>
        )
    },
    {
        title: 'Extra Shifts',
        dataIndex: 'extraShifts',
        key: 'extraShifts',
        render: (extraShifts, record) => {
            // Only show extra shifts input for FIXED employment type users
            const user = users.find(u => u.MobileNumber === selectedUser);
            const isFixedUser = user && user.EmploymentType === 'FIXED';
            
            if (!isFixedUser) {
                return <span style={{ color: themeConfig.text.mutedVeryLight }}>N/A</span>;
            }
            
            return (
                <InputNumber
                    min={0}
                    max={10}
                    value={extraShifts || 0}
                    onChange={(value) => handleExtraShiftsChange(record.key, value)}
                    disabled={record.disabled || isSlipGenerated}
                    style={{ width: 80 }}
                    placeholder="0"
                />
            );
        }
    }
  ];

  const handlePrint = async () => {
    if (!selectedUser || !selectedMonth) {
      messageApi.warning('Please select a user and a month first.');
      return;
    }

    if (monthlyData.length === 0) {
      messageApi.warning('Please load attendance data first.');
      return;
    }

    // Get user details for the header
    const user = users.find(u => u.MobileNumber === selectedUser);
    
    // Prepare print data with site names
    const printDataWithSiteNames = monthlyData.map(record => {
      const site = sites.find(s => s.id === record.siteId);
      return {
        ...record,
        siteName: site ? site.siteName : 'N/A'
      };
    });

    setPrintData(printDataWithSiteNames);
    setIsPrintModalVisible(true);
  };

  const handlePrintContent = () => {
    const printContent = document.getElementById('printable-attendance-content');
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    
    // Re-render the component after printing
    window.location.reload();
  };

  const handleDownloadCSV = () => {
    const user = users.find(u => u.MobileNumber === selectedUser);
    const isFixedUser = user && user.EmploymentType === 'FIXED';
    
    const headers = [
      'Date',
      'Day',
      'Status',
      'Site',
      ...(isFixedUser ? ['Extra Shifts'] : [])
    ];

    const csvContent = [
      headers.join(','),
      ...printData.map(record => [
        `"${record.date.format('YYYY-MM-DD')}"`,
        `"${record.date.format('ddd, DD MMM YYYY')}"`,
        `"${record.status === 'H' && record.narration ? `${record.status} (${record.narration})` : record.status}"`,
        `"${record.siteName}"`,
        ...(isFixedUser ? [`"${record.extraShifts || 0}"`] : [])
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `attendance_${user?.FullName}_${selectedMonth.format('MMMM_YYYY')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ backgroundColor: userTypeToggle === 'temporary' ? '#fff3cd' : 'transparent', minHeight: '100vh', padding: '16px' }}>
        {contextHolder}
        {/* User Type Toggle */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Space>
              <span>User Type:</span>
              <Button.Group>
                <Button 
                  type={userTypeToggle === 'permanent' ? 'primary' : 'default'}
                  onClick={() => {
                    setUserTypeToggle('permanent');
                    setSelectedUser(null);
                    setMonthlyData([]);
                  }}
                >
                  Permanent Users
                </Button>
                <Button 
                  type={userTypeToggle === 'temporary' ? 'primary' : 'default'}
                  onClick={() => {
                    setUserTypeToggle('temporary');
                    setSelectedUser(null);
                    setMonthlyData([]);
                  }}
                >
                  Temporary Users
                </Button>
              </Button.Group>
            </Space>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col>
                <Select
                    showSearch
                    placeholder={`Select a ${userTypeToggle === 'temporary' ? 'temporary' : 'permanent'} user`}
                    style={{ width: 300 }}
                    value={selectedUser}
                    onChange={(value) => {
                        setSelectedUser(value);
                        // Same reason as handleMonthChange: the loaded grid
                        // belongs to the previously selected worker.
                        setMonthlyData([]);
                        setIsSlipGenerated(false);
                    }}
                    optionFilterProp="label"
                    filterOption={(input, option) => {
                        const searchText = input.toLowerCase();
                        const optionText = option.label.toLowerCase();
                        return optionText.includes(searchText);
                    }}
                    notFoundContent={
                        users.length === 0 ? 
                        `No ${userTypeToggle === 'temporary' ? 'temporary' : 'permanent'} users available` : 
                        null
                    }
                >
                    {users.map((user) => {
                      const userLabel = userTypeToggle === 'temporary' 
                        ? `${user.FullName}${user.Designation ? ` (${user.Designation})` : ''} - ${user.MobileNumber}` 
                        : `${user.FullName} (${user.Job}) - ${user.MobileNumber}`;
                      return (
                        <Option key={user.MobileNumber} value={user.MobileNumber} label={userLabel}>
                          <span>
                            {userTypeToggle === 'permanent' && renderActivityDot(user.activeStatus)}
                            {userLabel}
                            {userTypeToggle === 'temporary' && <Tag color="orange" style={{ marginLeft: 8 }}>Temp</Tag>}
                          </span>
                        </Option>
                      );
                    })}
                </Select>
            </Col>
            <Col>
                <Select
                    placeholder="Select Month"
                    style={{ width: 200 }}
                    value={`${selectedMonth.year()}-${selectedMonth.month() + 1}`}
                    onChange={handleMonthChange}
                    options={generateMonthOptions()}
                />
            </Col>
            <Col>
                <Button type="primary" onClick={handleLoadAttendance} loading={loading}>Load Attendance</Button>
            </Col>
            <Col>
                <Button 
                    type="default"
                    onClick={handleMarkAllAsHoliday}
                    disabled={users.length === 0}
                >
                    Mark All Users as Holiday
                </Button>
            </Col>
            <Col>
                <Button 
                    icon={<PrinterOutlined />} 
                    onClick={handlePrint}
                    disabled={!selectedUser || monthlyData.length === 0}
                >
                    Print Attendance
                </Button>
            </Col>
            <Col>
                <Button 
                    type="default"
                    onClick={handleBulkMarking}
                    disabled={!selectedUser || monthlyData.length === 0 || isSlipGenerated}
                    style={{ backgroundColor: themeConfig.status.info, borderColor: themeConfig.status.info, color: 'var(--tertiary-text)' }}
                >
                    Bulk Marking
                </Button>
            </Col>
        </Row>

        {/* Activity Status Legend */}
        <Row style={{ marginBottom: 16 }}>
            <Col>
                <div style={{ fontSize: '12px', color: themeConfig.text.muted }}>
                    <strong>Activity Status:</strong>
                    <span style={{ marginLeft: '8px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: themeConfig.status.success, marginRight: '4px' }}></span>
                        Green: Active (present attendance this month)
                    </span>
                    <span style={{ marginLeft: '16px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: themeConfig.status.warning, marginRight: '4px' }}></span>
                        Orange: Inactive (no attendance this month, was active last month)
                    </span>
                    <span style={{ marginLeft: '16px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: themeConfig.status.error, marginRight: '4px' }}></span>
                        Red: Inactive (no attendance for 2+ months)
                    </span>
                </div>
            </Col>
        </Row>
        
        <Spin spinning={loading}>
            <Table
                columns={columns}
                dataSource={monthlyData}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 800 }}
            />
        </Spin>

        {monthlyData.length > 0 && (
            <div style={{ marginTop: 24, textAlign: 'right' }}>
                <Button type="primary" danger onClick={handleSubmit} loading={submitting} disabled={isSlipGenerated}>
                    Update All Attendance
                </Button>
            </div>
        )}

        {/* Print Modal */}
        <Modal
          title="Print Attendance Data"
          visible={isPrintModalVisible}
          onCancel={() => setIsPrintModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsPrintModalVisible(false)}>
              Cancel
            </Button>,
            <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
              Download CSV
            </Button>,
            <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintContent}>
              Print
            </Button>
          ]}
          width={1000}
          destroyOnClose
        >
          <div id="printable-attendance-content">
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <h2>Monthly Attendance Report</h2>
              {(() => {
                const user = users.find(u => u.MobileNumber === selectedUser);
                return (
                  <div>
                    <p><strong>Employee:</strong> {user?.FullName || 'N/A'} ({user?.Job || 'N/A'})</p>
                    <p><strong>Mobile:</strong> {user?.MobileNumber || 'N/A'}</p>
                    <p><strong>Month:</strong> {selectedMonth.format('MMMM YYYY')}</p>
                    <p><strong>Generated on:</strong> {new Date().toLocaleDateString()}</p>
                  </div>
                );
              })()}
            </div>
            
            <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${themeConfig.border.light}` }}>
              <thead>
                <tr style={{ backgroundColor: themeConfig.background.tableHeader }}>
                  <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Date</th>
                  <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Day</th>
                  <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Status</th>
                  <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Site</th>
                  {(() => {
                    const user = users.find(u => u.MobileNumber === selectedUser);
                    const isFixedUser = user && user.EmploymentType === 'FIXED';
                    return isFixedUser ? (
                      <th style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px', textAlign: 'left' }}>Extra Shifts</th>
                    ) : null;
                  })()}
                </tr>
              </thead>
              <tbody>
                {printData.map((record, index) => {
                  const user = users.find(u => u.MobileNumber === selectedUser);
                  const isFixedUser = user && user.EmploymentType === 'FIXED';
                  
                  return (
                    <tr key={record.key} style={{ backgroundColor: index % 2 === 0 ? themeConfig.background.tableRowOdd : themeConfig.background.tableRowEven }}>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.date.format('DD/MM/YYYY')}</td>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.date.format('ddd')}</td>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>
                        <span style={{ 
                          color: record.status === 'P' ? themeConfig.status.success : 
                                 record.status === 'A' ? themeConfig.status.error : 
                                 record.status === 'H' ? themeConfig.status.warning :
                                 record.status === 'NOT_MARKED' ? themeConfig.text.mutedLight : themeConfig.status.info
                        }}>
                          {(() => {
                            // For fixed users, map partial present statuses to P for display
                            if (isFixedUser && ['P/2', 'P1/4', 'P1/2', 'PP', 'PP1/2', 'PPP', 'PPP1/2', 'PPPP'].includes(record.status)) {
                              return 'P';
                            }
                            // Show narration in braces for holiday status
                            if (record.status === 'H' && record.narration) {
                              return `${record.status} (${record.narration})`;
                            }
                            return record.status;
                          })()}
                        </span>
                      </td>
                      <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.siteName}</td>
                      {isFixedUser && (
                        <td style={{ border: `1px solid ${themeConfig.border.light}`, padding: '8px' }}>{record.extraShifts || 0}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <p><strong>Total Days:</strong> {printData.length}</p>
                  <p><strong>Present Days:</strong> {printData.filter(r => {
                    ['P/2', 'P', 'P1/4', 'P1/2', 'PP', 'PP1/2', 'PPP', 'PPP1/2', 'PPPP'].includes(r.status)
                  }).length}</p>
                  <p><strong>Absent Days:</strong> {printData.filter(r => r.status === 'A').length}</p>
                  <p><strong>Holiday Days:</strong> {printData.filter(r => r.status === 'H').length}</p>
                </div>
                <div>
                  {(() => {
                    const user = users.find(u => u.MobileNumber === selectedUser);
                    const isFixedUser = user && user.EmploymentType === 'FIXED';
                    return (
                      <>
                        {isFixedUser && (
                          <p><strong>Total Extra Shifts:</strong> {printData.reduce((sum, r) => sum + (r.extraShifts || 0), 0)}</p>
                        )}
                        <p><strong>Unmarked Days:</strong> {printData.filter(r => r.status === 'NOT_MARKED').length}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {/* Holiday Modal */}
        <Modal
          title="Mark All Users as Holiday"
          visible={isHolidayModalVisible}
          onOk={handleConfirmHoliday}
          onCancel={handleCancelHoliday}
          okText="Mark as Holiday"
          cancelText="Cancel"
          okButtonProps={{ disabled: !selectedHolidayDate }}
          width={600}
        >
          <div>
            <p>This will mark ALL active users as "H" (Holiday) for the selected date.</p>
            <p style={{ marginBottom: 16 }}>Please select the date:</p>
            <DatePicker
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Select date"
              format="YYYY-MM-DD"
              value={selectedHolidayDate}
              onChange={handleHolidayDateChange}
            />
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Paid Holiday:
              </label>
              <Select
                style={{ width: '100%' }}
                value={holidayIsPaid ? 'paid' : 'unpaid'}
                onChange={(value) => setHolidayIsPaid(value === 'paid')}
              >
                <Option value="paid">Yes (H(P))</Option>
                <Option value="unpaid">No (H(A))</Option>
              </Select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Narration (Optional):
              </label>
              <TextArea
                placeholder="Enter reason for holiday"
                value={holidayNarration}
                onChange={(e) => setHolidayNarration(e.target.value)}
                rows={3}
                maxLength={200}
              />
            </div>
          </div>
        </Modal>

        {/* Bulk Marking Modal */}
        <Modal
          title="Bulk Marking for Unmarked Dates"
          visible={isBulkMarkingModalVisible}
          onOk={handleConfirmBulkMarking}
          onCancel={handleCancelBulkMarking}
          okText="Apply Status"
          cancelText="Cancel"
          okButtonProps={{ disabled: !bulkMarkingStatus }}
          width={500}
        >
          <div>
            <p style={{ marginBottom: 16 }}>
              This will update all unmarked dates (NOT_MARKED) for the selected user to the chosen status.
            </p>
            <p style={{ marginBottom: 8, fontWeight: 500 }}>
              Unmarked dates count (excluding future dates): <strong>
                {monthlyData.filter(item => 
                  item.status === 'NOT_MARKED' && 
                  !item.disabled && 
                  dayjs(item.key).isSameOrBefore(dayjs(), 'day')
                ).length}
              </strong>
            </p>
            <p style={{ marginBottom: 16 }}>Please select the status to apply:</p>
            <Select
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Select status"
              value={bulkMarkingStatus}
              onChange={setBulkMarkingStatus}
            >
              {(() => {
                const user = users.find(u => u.MobileNumber === selectedUser);
                const isFixedUser = user && user.EmploymentType === 'FIXED';
                const availableStatuses = isFixedUser 
                  ? ["A", "P", "H"] 
                  : ["A", "P/2", "P", "P1/4", "P1/2", "PP", "PP1/2", "PPP", "PPP1/2", "PPPP", "H"];
                
                return availableStatuses.map(status => (
                  <Option key={status} value={status}>{status}</Option>
                ));
              })()}
            </Select>
            
            <p style={{ marginBottom: 8, fontWeight: 500 }}>Site (Optional):</p>
            <Select
              style={{ width: '100%' }}
              placeholder="Select site (optional)"
              value={bulkMarkingSite}
              onChange={setBulkMarkingSite}
              allowClear
            >
              {sites.map(site => (
                <Option key={site.id} value={site.id}>
                  {site.siteName}
                </Option>
              ))}
            </Select>
            
            <p style={{ marginTop: 16, color: themeConfig.text.muted, fontSize: '12px' }}>
              Note: Changes will be applied to the table but won't be saved until you click "Update All Attendance" button.
            </p>
          </div>
        </Modal>
    </div>
  );
};

export default AttendanceManagement; 